<template>
  <div>
    <card :class="`card-user card-contact ${classes}`">
      <div
        slot="image"
        class="image"
        :style="
          `background-color: ${randomColor({
            luminosity: 'light',
            seed: contact.id
          })}`
        "
      >
        <div class="text-right">
          <button
            class="btn btn-link mr-2"
            :style="`color: ${lightDarkColorFromBg(randomColor({
            luminosity: 'light',
            seed: contact.id
          }), '#fff', '#333')}`"
            title="Edit contact"
            v-on:click="openEditContactModal"
          >
            <i class="fa fa-ellipsis-v fa-2x"></i>
          </button>

          <button
            class="btn btn-link mr-2"
            :style="`color: ${lightDarkColorFromBg(randomColor({
            luminosity: 'light',
            seed: contact.id
          }), '#fff', '#333')}`"
            title="Delete contact"
            v-on:click="openDeleteContactAlert"
          >
            <i class="fa fa-trash-o fa-2x"></i>
          </button>
        </div>
      </div>
      <div>
        <div class="author">
          <router-link v-if="link" :to="link">
            <img
              width="124"
              height="124"
              class="avatar border-gray"
              :src="contact.avatarUrl || '/img/default-avatar.png'"
              :alt="
                `Profile photo for ${contact.firstName} ${contact.lastName}`
              "
            />
            <h5 class="title">
              {{ contact.firstName }}
              {{ contact.lastName }}
            </h5>
          </router-link>
          <div v-else>
            <img
              width="124"
              height="124"
              class="avatar border-gray"
              :src="contact.avatarUrl || '/img/default-avatar.png'"
              :alt="
                `Profile photo for ${contact.firstName} ${contact.lastName}`
              "
            />
            <h5 class="title">
              {{ contact.firstName }}
              {{ contact.lastName }}
            </h5>
          </div>
          <div class="description">
            <div v-if="contact.organisations && contact.organisations.length">
              <span
                class="badge badge-info mr-1"
                style="white-space: normal"
                v-for="(organisation,
                organisationIndex) in contact.organisations"
                :key="`organisation-${organisationIndex}`"
                >{{ organisation.role }} at {{ organisation.name }}</span
              >
            </div>
          </div>
        </div>
        <div class="description">
          <ul class="fa-ul">
            <li v-if="contact.catchupSchedule">
              <i class="fa-li fa fa-refresh"></i>
              Catch-up reminders every
              {{ contact.catchupSchedule.frequency }}
              {{
                contact.catchupSchedule.interval
                  | pluralize(
                    contact.catchupSchedule.interval,
                    contact.catchupSchedule.frequency
                  )
              }}
            </li>
            <li v-else>
              <i class="fa-li fa fa-circle"></i>
              No catch-up reminders
            </li>

            <li v-if="contact.previousCatchup">
              <i class="fa-li fa fa-calendar-minus-o"></i>
              <router-link :to="`/catch-ups/${contact.previousCatchup.id}`">
                Last catch-up
                {{
                  contact.previousCatchup.date
                    | moment("calendar", "now", calendarFormats)
                }}
              </router-link>
            </li>
            <li v-else>
              <i class="fa-li fa fa-calendar-minus-o"></i>
              No previous catch-ups
            </li>

            <li v-if="contact.nextCatchup">
              <i class="fa-li fa fa-calendar-plus-o"></i>
              <router-link :to="`/catch-ups/${contact.nextCatchup.id}`">
                Next catch-up
                {{
                  contact.nextCatchup.date
                    | moment("calendar", "now", calendarFormats)
                }}
              </router-link>
            </li>
            <li
              v-else-if="
                contact.catchupSchedule &&
                  !contact.nextCatchup &&
                  !dateIsInFuture(contact.catchupDueOn)
              "
            >
              <router-link
                :to="`/contacts/${contact.id}?create_catchup=true`"
                class="text-danger"
              >
                <i class="fa-li fa fa-exclamation-triangle"></i>
                Catch-up overdue
              </router-link>
            </li>
            <li
              v-else-if="
                contact.catchupSchedule &&
                  !contact.nextCatchup &&
                  dateIsSoon(contact.catchupDueOn)
              "
              v-bind:class="{ 'text-warning': contact.catchupSchedule }"
            >
              <i class="fa-li fa fa-exclamation-triangle"></i>
              Catch-up due soon
            </li>
            <li v-else v-bind:class="{ 'text-info': contact.catchupSchedule }">
              <router-link
                :to="`/contacts/${contact.id}?create_catchup=true`"
                class="text-info"
              >
                <i class="fa-li fa fa-calendar-plus-o"></i>
                No scheduled catch-ups
                <span v-if="contact.catchupSchedule"
                  >(next due
                  {{ contact.catchupDueOn | moment("DD MMM YYYY") }})</span
                >
              </router-link>
            </li>
          </ul>
        </div>
      </div>
      <!-- <div slot="footer" class="button-container">
        <n-button href="#" type="neutral" icon round size="lg">
          <i class="fa fa-facebook-square"></i>
        </n-button>
        <n-button href="#" type="neutral" icon round size="lg">
          <i class="fa fa-twitter"></i>
        </n-button>
        <n-button href="#" type="neutral" icon round size="lg">
          <i class="fa fa-google-plus-square"></i>
        </n-button>
      </div>-->
    </card>

    <modal
      :show.sync="showEditContactModal"
      headerClasses="justify-content-center"
      modalClasses="modal-lg"
    >
      <h4 slot="header" class="title title-up">Edit contact</h4>

      <div v-if="editingContact">
        <strong class="label">Personal information</strong>
        <div class="form-row">
          <div class="col">
            <fg-input
              v-model="editingContact.firstName"
              type="text"
              name="firstName"
              class="form-control-lg"
              placeholder="First name"
              addon-left-icon="now-ui-icons users_circle-08"
            ></fg-input>
          </div>
          <div class="col">
            <fg-input
              v-model="editingContact.lastName"
              type="text"
              name="lastName"
              class="form-control-lg"
              placeholder="Last name"
              addon-left-icon="now-ui-icons text_caps-small"
            ></fg-input>
          </div>
        </div>

        <!-- email addresses -->
        <strong class="label">Email</strong>
        <div
          class="form-row"
          v-for="(email, emailIndex) in editingContact.emails"
          :key="`email-${emailIndex}`"
        >
          <div class="col-sm-5">
            <fg-input
              v-model="email.address"
              type="email"
              name="emailAddress"
              class="form-control-lg"
              placeholder="Address"
              addon-left-icon="now-ui-icons ui-1_email-85"
            ></fg-input>
          </div>

          <div class="col-sm-3">
            <fg-input
              v-model="email.label"
              type="text"
              name="label"
              class="form-control-lg"
              placeholder="Label"
              addon-left-icon="now-ui-icons shopping_tag-content"
            ></fg-input>
          </div>

          <div class="col-sm-3">
            <checkbox
              v-model="email.isPrimary"
              :disabled="!email.isPrimary && editingContactHasPrimaryEmail"
            >
              <small>Primary</small>
            </checkbox>
          </div>

          <div class="col-sm-1">
            <button
              class="btn btn-link btn-sm"
              v-on:click="editingContact.emails.splice(emailIndex, 1)"
            >
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </div>
        <div class="text-right">
          <button
            class="btn btn-default"
            v-on:click="
              editingContact.emails.push({
                address: null,
                label: null,
                isPrimary: false
              })
            "
          >
            Add email address
            <i class="fa fa-plus ml-2"></i>
          </button>
        </div>

        <!-- phone numbers -->
        <strong class="label">Phone</strong>
        <div
          class="form-row"
          v-for="(phone, phoneIndex) in editingContact.phones"
          :key="`phone-${phoneIndex}`"
        >
          <div class="col-sm-5">
            <fg-input
              v-model="phone.number"
              type="tel"
              name="phoneNumber"
              class="form-control-lg"
              placeholder="Number"
              addon-left-icon="fa fa-phone"
            ></fg-input>
          </div>

          <div class="col-sm-3">
            <fg-input
              v-model="phone.label"
              type="text"
              name="label"
              class="form-control-lg"
              placeholder="Label"
              addon-left-icon="now-ui-icons shopping_tag-content"
            ></fg-input>
          </div>

          <div class="col-sm-3">
            <checkbox
              v-model="phone.isPrimary"
              :disabled="!phone.isPrimary && editingContactHasPrimaryPhone"
            >
              <small>Primary</small>
            </checkbox>
          </div>

          <div class="col-sm-1">
            <button
              class="btn btn-link btn-sm"
              v-on:click="editingContact.phones.splice(phoneIndex, 1)"
            >
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </div>
        <div class="text-right">
          <button
            class="btn btn-default"
            v-on:click="
              editingContact.phones.push({
                number: null,
                label: null,
                isPrimary: false
              })
            "
          >
            Add phone number
            <i class="fa fa-plus ml-2"></i>
          </button>
        </div>

        <!-- organisations -->
        <strong class="label">Organisations</strong>
        <div
          class="form-row"
          v-for="(organisation,
          organisationIndex) in editingContact.organisations"
          :key="`organisation-${organisationIndex}`"
        >
          <div class="col-sm-6">
            <fg-input
              v-model="organisation.name"
              type="text"
              name="organisationName"
              class="form-control-lg"
              placeholder="Organisation name"
              addon-left-icon="fa fa-building"
            ></fg-input>
          </div>

          <div class="col-sm-5">
            <fg-input
              v-model="organisation.role"
              type="text"
              name="organisationRole"
              class="form-control-lg"
              placeholder="Role"
              addon-left-icon="fa fa-user"
            ></fg-input>
          </div>

          <div class="col-sm-1">
            <button
              class="btn btn-link btn-sm"
              v-on:click="
                editingContact.organisations.splice(organisationIndex, 1)
              "
            >
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </div>
        <div class="text-right">
          <button
            class="btn btn-default"
            v-on:click="
              editingContact.organisations.push({
                name: null,
                role: null
              })
            "
          >
            Add organisation
            <i class="fa fa-plus ml-2"></i>
          </button>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group row">
              <div class="col-8">
                <strong>
                  <label for="catchupEnabled" class="col-form-label"
                    >Catch-up reminders</label
                  >
                </strong>
              </div>
              <div class="col-4 text-right">
                <n-switch
                  v-model="editingContactCatchupEnabled"
                  name="catchupEnabled"
                  type="primary"
                  on-text="ON"
                  off-text="OFF"
                  class="mt-2"
                ></n-switch>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div v-if="editingContactCatchupEnabled">
              <div class="form-group row">
                <label class="col-4 col-form-label">Every</label>

                <div class="col-3">
                  <select
                    class="form-control"
                    v-model="editingContact.catchupSchedule.frequency"
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8</option>
                    <option value="9">9</option>
                    <option value="10">10</option>
                    <option value="11">11</option>
                    <option value="12">12</option>
                  </select>
                </div>

                <div class="col-5">
                  <select
                    class="form-control"
                    v-model="editingContact.catchupSchedule.interval"
                  >
                    <option value="day">
                      <span v-if="editingContact.catchupSchedule.frequency > 1"
                        >days</span
                      >
                      <span v-else>day</span>
                    </option>
                    <option value="week">
                      <span v-if="editingContact.catchupSchedule.frequency > 1"
                        >weeks</span
                      >
                      <span v-else>week</span>
                    </option>
                    <option value="month">
                      <span v-if="editingContact.catchupSchedule.frequency > 1"
                        >months</span
                      >
                      <span v-else>month</span>
                    </option>
                    <option value="year">
                      <span v-if="editingContact.catchupSchedule.frequency > 1"
                        >years</span
                      >
                      <span v-else>year</span>
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <strong class="label">Notes</strong>
          <textarea
            class="form-control"
            id="note"
            rows="5"
            placeholder="Add notes about this contact here..."
            v-model="editingContact.note"
          ></textarea>
          <small class="form-text text-muted">
            Markdown supported (
            <a
              href="https://www.markdownguide.org/basic-syntax/"
              target="_blank"
              >syntax guide</a
            >)
          </small>
        </div>
      </div>

      <template slot="footer">
        <n-button @click.native="showEditContactModal = false">
          <i class="fa fa-times"></i>&nbsp; Cancel
        </n-button>
        <n-button type="success" @click.native="saveContact" :disabled="saving">
          <span v-if="!saving"> <i class="fa fa-check"></i>&nbsp; Save </span>
          <span v-else>
            <i class="fa fa-circle-o-notch fa-spin"></i>&nbsp; Please
            wait&hellip;
          </span>
        </n-button>
      </template>
    </modal>
  </div>
</template>

<script>
import swal from "sweetalert2";
import moment from "moment";

import { Modal } from "src/components";
import { Checkbox, Switch } from "src/components/index";
import { request } from "http";

export default {
  components: {
    Modal,
    Checkbox,
    [Switch.name]: Switch
  },
  props: {
    contact: {
      type: Object,
      default: null
    },
    link: {
      type: String,
      default: "#"
    },
    classes: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      showEditContactModal: false,
      saving: false,
      editingContact: null,
      calendarFormats: {
        sameDay: "[today]",
        nextDay: "[tomorrow]",
        nextWeek: "dddd",
        lastDay: "[yesterday]",
        lastWeek: "[last] dddd",
        sameElse: "DD MMM YYYY"
      },
      editingContactCatchupEnabled: false
    };
  },
  computed: {
    editingContactHasPrimaryEmail: function() {
      let result = false;
      if (this.editingContact && this.editingContact.emails) {
        this.editingContact.emails.forEach((item, index) => {
          if (item.isPrimary === true) {
            result = true;
          }
        });
      }

      return result;
    },
    editingContactHasPrimaryPhone: function() {
      let result = false;
      if (this.editingContact && this.editingContact.phones) {
        this.editingContact.phones.forEach((item, index) => {
          if (item.isPrimary === true) {
            result = true;
          }
        });
      }

      return result;
    }
  },
  watch: {
    editingContactCatchupEnabled: function(val, oldVal) {
      if (val === true) {
        if (!this.editingContact.catchupSchedule) {
          this.editingContact.catchupSchedule = {
            interval: "month",
            frequency: 3
          };
        }
      }
    }
  },
  methods: {
    lightDarkColorFromBg(bgColor, lightColor, darkColor) {
      var color = (bgColor.charAt(0) === '#') ? bgColor.substring(1, 7) : bgColor;
      var r = parseInt(color.substring(0, 2), 16); // hexToR
      var g = parseInt(color.substring(2, 4), 16); // hexToG
      var b = parseInt(color.substring(4, 6), 16); // hexToB
      return (((r * 0.299) + (g * 0.587) + (b * 0.114)) > 186) ?
        darkColor : lightColor;
    },
    dateIsInFuture: function(date) {
      return moment(date).isAfter(moment());
    },
    dateIsSoon: function(date) {
      const soon = moment().add(2, "weeks");
      return (
        moment(date).isAfter(moment()) && moment(date).isSameOrBefore(soon)
      );
    },
    randomColor: function(args) {
      return this.$randomColor(args);
    },
    openEditContactModal: function() {
      this.editingContact = JSON.parse(JSON.stringify(this.contact));

      // set catchupEnabled
      if (this.editingContact.catchupSchedule) {
        this.editingContactCatchupEnabled = true;
      } else {
        this.editingContactCatchupEnabled = false;
      }

      this.showEditContactModal = true;
    },
    saveContact: function() {
      const instance = this;
      instance.saving = true;

      const requestBody = {};

      // first name
      if (
        instance.editingContact.firstName &&
        instance.editingContact.firstName !== instance.contact.firstName
      ) {
        requestBody.firstName = instance.editingContact.firstName;
      }

      // last name
      if (
        instance.editingContact.lastName &&
        instance.editingContact.lastName !== instance.contact.lastName
      ) {
        requestBody.lastName = instance.editingContact.lastName;
      }

      // note
      if (
        instance.editingContact.note &&
        instance.editingContact.note !== instance.contact.note
      ) {
        requestBody.note = instance.editingContact.note;
      }

      // emails
      if (
        instance.editingContact.emails &&
        instance.editingContact.emails.length
      ) {
        requestBody.emails = [];

        // remove any empty entries
        instance.editingContact.emails.forEach(item => {
          if (item.address && item.address.trim() !== "") {
            requestBody.emails.push(item);
          }
        });

        if (requestBody.emails.length < 1) {
          delete requestBody.emails;
        }
      }

      // phones
      if (
        instance.editingContact.phones &&
        instance.editingContact.phones.length
      ) {
        requestBody.phones = [];

        // remove any empty entries
        instance.editingContact.phones.forEach(item => {
          if (item.number && item.number.trim() !== "") {
            requestBody.phones.push(item);
          }
        });

        if (requestBody.phones.length < 1) {
          delete requestBody.phones;
        }
      }

      // organisations
      if (
        instance.editingContact.organisations &&
        instance.editingContact.organisations.length
      ) {
        requestBody.organisations = [];

        // remove any empty entries
        instance.editingContact.organisations.forEach(item => {
          if (
            (item.name && item.name.trim() !== "") ||
            (item.role && item.role.trim() !== "")
          ) {
            requestBody.organisations.push(item);
          }
        });

        if (requestBody.organisations.length < 1) {
          delete requestBody.organisations;
        }
      }

      // catchup schedule
      if (instance.editingContactCatchupEnabled) {
        requestBody.catchupSchedule = instance.editingContact.catchupSchedule;
      } else {
        requestBody.catchupSchedule = null;
      }

      instance.axios
        .patch(`/v1/contacts/${instance.contact.id}`, requestBody)
        .then(response => {
          // delay to wait for API's afterCreate hook
          setTimeout(() => {
            instance.$emit("contact-updated", response.data);

            instance.showEditContactModal = false;
            instance.saving = false;

            this.$notify({
              message: `<b>${instance.contact.firstName} ${instance.contact.lastName}</b> updated`,
              timeout: 5000,
              icon: "fa fa-check",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success"
            });
          }, 1000);
        })
        .catch(err => {
          instance.saving = false;
          // TODO: handle the error
        });
    },
    openDeleteContactAlert: function() {
      const instance = this;
      swal({
        title: "Are you sure?",
        text: `${instance.contact.firstName} and all related catch-ups will be deleted`,
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-default btn-fill",
        confirmButtonText: `Yes, delete ${instance.contact.firstName}`,
        buttonsStyling: false,
        reverseButtons: true
      }).then(function(result) {
        if (result.value) {
          instance.axios
            .delete(`/v1/contacts/${instance.contact.id}`)
            .then(response => {
              instance.$emit("contact-deleted", instance.contact);

              swal({
                title: "Deleted!",
                text: "Contact deleted.",
                type: "success",
                confirmButtonClass: "btn btn-success btn-fill",
                buttonsStyling: false
              });
            })
            .catch(err => {
              // TODO: handle the error
            });
        }
      });
    }
  }
};
</script>

<style></style>
